import { useEffect } from 'react'
import { fonts, styled } from '@portfolio/styles'
import { Container, Link, Media, PortableText } from '@portfolio/components'
import { NavigationNestedItem } from '@portfolio/models'

type NestedNavigationProps = {
  navItem: NavigationNestedItem
  theme?: 'transparent-dark' | 'transparent-light'
  isOpen?: boolean
  onMouseEnter?: () => void
  onMouseLeave?: (e: React.MouseEvent) => void
  onClose?: () => void
  id?: string
}

const NestedNavigationContainer = styled(Container, {
  position: 'absolute',
  paddingTop: '$32',
  paddingBottom: '$30',
  top: '$headerHeight',
  left: 0,
  width: '100%',
  height: '660px',
  opacity: 0,
  visibility: 'hidden',
  transition: 'all 0.2s ease',
  zIndex: '$dropdown',

  variants: {
    isOpen: {
      true: {
        opacity: 1,
        visibility: 'visible',

        '& ~ [data-chevron="nested-navigation-chevron"]': {
          opacity: 1,
          transform: 'rotateX(0deg)',
        },
      },
      false: {
        opacity: 0,
        visibility: 'hidden',
      },
    },
  },
})

const NestedDropdown = styled('ul', {
  height: '100%',
  display: 'grid',
  gridTemplateColumns: '2fr 1fr',
  gridTemplateRows: '3fr 1fr',
  gridGap: '$32',
  margin: '0 auto',

  '@lg': {
    // max navigation width minus padding from header
    maxWidth: 'calc($maxNavigationWidth - 2 * $30)',
  },
})

const LinkGroupContainer = styled('div', {
  gridColumn: 'span 2',
  gridRow: 'span 1',
  rowGap: '$64',
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(160px, auto-fill))',
  gridTemplateRows: 'repeat(2, 200px)',
  gridAutoFlow: 'column',
  gridGap: '$32',
})

const LinksContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$16',

  // If container height is small, allow vertical stacking
  '@container (max-height: 400px)': {
    gap: '$8',
  },
})

const NestedDropdownItem = styled('li', {
  display: 'block',
  width: '100%',
  minWidth: 0,
})

const NestedDropdownLink = styled(Link, {
  ...fonts.ui05,
  display: 'flex',
  gap: '$16',
  alignItems: 'center',
  transition: 'background-color 0.2s ease',
  width: 'fit-content',
  wordWrap: 'break-word',
  overflowWrap: 'break-word',
  hyphens: 'auto',

  variants: {
    theme: {
      'transparent-dark': {
        color: '$black',
        borderBottom: '1px solid $black',
      },
      'transparent-light': {
        color: '$white',
        borderBottom: '1px solid $white',
      },
    },
  },
})

const GroupLabel = styled('div', {
  projectFont: 'heading08',
  marginBottom: '$32',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',

  variants: {
    theme: {
      'transparent-dark': {
        color: '$gray700',
      },
      'transparent-light': {
        color: '$gray300',
      },
    },
  },
})

const FlexibleGridItem = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gridRow: 1,

  '&:last-child': {
    gridRow: '2',
  },
})

const MediaSectionContainer = styled('div', {
  position: 'relative',
  gridColumn: '3',
  gridRow: 'span 2',
  minWidth: '330px',
  height: 'fit-content',
})

const MediaSectionHeading = styled('h3', {
  projectFont: 'heading08',
  marginBottom: '$24',

  variants: {
    theme: {
      'transparent-dark': {
        color: '$black',
      },
      'transparent-light': {
        color: '$white',
      },
    },
  },
})

const MediaContainer = styled('div', {
  gridArea: '2/2',
  position: 'relative',

  '& img, & video': {
    borderRadius: '$rBoxes',
  },
})

const GradientOverlay = styled('div', {
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  height: '50%',
  background:
    'linear-gradient(to bottom, transparent 0%, rgba(0, 0, 0, 0.6) 100%)',
  pointerEvents: 'none',
  borderRadius: '0 0 $rBoxes $rBoxes',
  zIndex: '$content',
})

const StyledPortableTextWrapper = styled(Container, {
  position: 'absolute',
  bottom: '0',
  padding: '$24',
  textWrap: 'pretty',
  zIndex: '$content',
  pointerEvents: 'none',
  color: '$white',
  '& p': {
    projectFont: 'heading08',
    color: '$white_07',
    '& strong': {
      color: '$white',
    },
  },
})

const FooterCtaContainer = styled('div', {
  paddingRight: '$heading08',
  gridColumn: 'span 2',
  alignSelf: 'end',
  gridRow: 'span 1',
  display: 'flex',
  alignItems: 'center',
  gap: '$24',
})

const StyledMediaLink = styled(Link, {
  position: 'absolute',
  zIndex: '$content',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'grid',
  cursor: 'pointer',
  gridArea: '2/2',
})

export const NestedNavigationPanel = ({
  navItem,
  theme = 'transparent-light',
  isOpen = false,
  onMouseEnter,
  onMouseLeave,
  onClose,
  id,
}: NestedNavigationProps) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose?.()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose])

  return (
    <NestedNavigationContainer
      className="nested-navigation-container"
      data-open={isOpen}
      spaceSize="medium"
      isOpen={isOpen}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      id={id}
      aria-hidden={!isOpen}
    >
      {(navItem.linkGroups || navItem.ctaSection) && (
        <NestedDropdown>
          <LinkGroupContainer>
            {navItem.linkGroups?.map((group) => {
              return (
                <FlexibleGridItem key={group._key}>
                  {group.groupLabel && (
                    <GroupLabel theme={theme}>{group.groupLabel}</GroupLabel>
                  )}
                  <LinksContainer>
                    {group.links?.map((link) => (
                      <NestedDropdownItem key={link._key}>
                        {link.link && (
                          <NestedDropdownLink {...link.link} theme={theme}>
                            {link.title}
                          </NestedDropdownLink>
                        )}
                      </NestedDropdownItem>
                    ))}
                  </LinksContainer>
                </FlexibleGridItem>
              )
            })}
          </LinkGroupContainer>

          {navItem.ctaSection?.media && (
            <MediaSectionContainer>
              <MediaSectionHeading theme={theme} id={`${id}-media-heading`}>
                {navItem.ctaSection.title}
              </MediaSectionHeading>
              {navItem.ctaSection.link && (
                <StyledMediaLink
                  {...navItem.ctaSection.link}
                  tabIndex={isOpen ? 0 : -1}
                  aria-labelledby={`${id}-media-heading`}
                ></StyledMediaLink>
              )}
              <MediaContainer>
                {navItem.ctaSection.media.mediaPayload && (
                  <Media
                    mediaPayload={navItem.ctaSection.media.mediaPayload}
                    alt={navItem.ctaSection.media.alt}
                    priority={false}
                    hardcropRatio="portrait2"
                    sizes="(min-width: 1000px) 330px, 290px"
                    controlsPosition="top-right"
                  />
                )}
                <GradientOverlay />
              </MediaContainer>
              <StyledPortableTextWrapper>
                <PortableText
                  appearance="plain"
                  value={navItem.ctaSection.mediaCopy}
                />
              </StyledPortableTextWrapper>
            </MediaSectionContainer>
          )}
          {navItem.ctaSection?.footerCta && (
            <FooterCtaContainer>
              <span>{navItem.ctaSection.footerCta.footerCtaCopy}</span>
              {navItem.ctaSection.footerCta.footerCta && (
                <Link
                  appearance={`${
                    theme === 'transparent-dark'
                      ? 'containedBlack'
                      : 'containedTransparent'
                  }`}
                  {...navItem.ctaSection.footerCta.footerCta}
                >
                  {navItem.ctaSection.footerCta.footerCtaLabel}
                </Link>
              )}
            </FooterCtaContainer>
          )}
        </NestedDropdown>
      )}
    </NestedNavigationContainer>
  )
}
